import { ITeamInteractionRepository } from '@/app/core/IRepositories/ITeamInteractionRepository';
import { FileModel } from '@/app/core/Models/file';
import { InteractionDirection, InteractionStatus, InteractionThread, InteractionTranscription, RecentInteraction } from '@/app/core/Models/Interaction';
import { TeamMember } from '@/app/core/Models/TeamMember';
import { InputCreateRoom, TeamMessageBody } from '@/app/Data/DTO/TeamChatMessageDto';
import { AppState } from '@/app/State/AppState';
import { CloseAllTeamChatThreadsAction, CloseTeamChatThreadAction, DeleteTeamChatMessageAction, FetchTeamChatMessagesAction, FetchTeamRecentMessagesAction, OpenTeamChatThreadAction, ReactToTeamChatMessageAction, ResendTeamChatMessageAction, SendTeamChatMessageAction, SendTeamChatMessageFailAction, SendTeamChatMessageSuccessAction, UpdateTeamChatMessageStatusAction, ExpandMessageWindowAction, ExpandMessageWindowSuccessAction, ExpandMessageWindowFailAction, CleanupConversationMemoryAction } from '@/app/State/TeamInbox/teamInbox.action';
import { selectActiveUser, selectTeamChatActiveUsers, selectTeamInboxState } from '@/app/State/TeamInbox/teamInbox.selector';
import { participantRooms, selectedRoom, tenantRooms } from "@/app/State/Room/room.selector";
import { selectTenantProfile } from '@/app/State/Tenant/tenant.selector';
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Observer, catchError, combineLatest, distinctUntilChanged, filter, first, firstValueFrom, map, of, sampleTime, shareReplay, switchMap, takeUntil, tap, lastValueFrom, debounceTime, Subject } from 'rxjs';
import { UsersViewModel } from './usersViewModel';
import { CreateTeamMessageUseCase } from '@/app/core/usecases/team-messaging/CreateTeamMessageUseCase';
import { SnackbarService } from '@/app/Utilities/snackbar/snackbar.service';
import { ErrorApiReponse } from '@/app/Data/services/Networking/ApiResponse';
import { IRoom } from '../team-chat-center/team-chat-room/team-chat-room.interface';

import { RightPanelIndexes, SetRightPanelActiveWindowAction } from '@/app/State/mediaObserver/media.observer.action';
import { AddRoomParticipantAction, CreateRoomAction, FetchParticipantRoomsAction, FetchTenantRoomsAction, RemoveRoomParticipantAction, SetActiveRoomAction, UpdateRoomAction } from '@/app/State/Room/room.action';
@Injectable({
  providedIn: 'root',
})
export class TeamChatViewModel implements OnDestroy {
  constructor(
    private store: Store<AppState>,
    private usersViewModel: UsersViewModel,
    private teamInteractionRepository: ITeamInteractionRepository,
    private createTeamMessageUseCase: CreateTeamMessageUseCase,
    private snackBarService: SnackbarService
  ) {
    // Set up debounced scroll-to-load handler
    this.scrollSubject.pipe(
      debounceTime(300), // Debounce for 300ms to prevent rapid API calls
      distinctUntilChanged(),
      switchMap(userId => 
        this.teamInboxState$.pipe(
          first(),
          map(state => ({ userId, state }))
        )
      ),
      takeUntil(this.destroy$) // Properly cleanup subscription
    ).subscribe(({ userId, state }) => {
      // Check if already expanding to prevent duplicate requests
      if (state.threadInteractions.expandingWindow) {
        console.log('Already expanding window, skipping debounced scroll request');
        return;
      }

      // Check if we've hit the message cap
      if ((state.threadInteractions.windowSize || 100) >= 1000) {
        console.log('Message window at maximum size (1000), cannot expand further');
        return;
      }

      const nextPage = state.threadInteractions.page + 1;
      
      // Dispatch with proper error handling
      this.store.dispatch(ExpandMessageWindowAction({ 
        payload: { 
          userId, 
          additionalSize: 50,
          page: nextPage
        } 
      }));
    });
  }

  init() {
    this.fetchTenantRooms();
    this.fetchProfileRooms();
    this.usersViewModel.fetchTeamMembers();
  }

  initialized = false
  teamMembers$ = this.usersViewModel.teamMembers$.pipe(
    filter(u => !!u?.length), tap(() => this.teamMembersLoaded$.next(true)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  teamMembersLoaded$ = new BehaviorSubject(false)
  teamInboxState$ = this.store.select(selectTeamInboxState).pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  recentInteractionsPage$ = new BehaviorSubject(0)
  recentInteractions$ = this.teamInteractionRepository.getLocalTeamRecentMessages().pipe(
    switchMap(interactions =>
      this.interactionType$.asObservable().pipe(
        map(type => interactions.filter(({ is_room }) =>
          type === 'all' || type === 'groups' && is_room || type === 'personal' && !is_room
        ))
      )
    ),
    switchMap(recentInteractions =>
      this.teamMembers$.pipe(
        map(users =>
          recentInteractions.map(recentInteraction => Object.assign(new RecentInteraction(), {
            ...recentInteraction,
            metadata: { online: users.find(u => u.user_id == recentInteraction.interaction.user_id)?.active }
          }))
        )
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  yobiOneRecentInteractions$ = this.teamInteractionRepository.getYobiOneTeamMessages().pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  unreadMesages$ = this.recentInteractions$.pipe(
    map(interactions => interactions.filter(i => i.interaction.status == 'unread').length),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  // Add cache tracking for cleanup
  private conversationCaches = new Map<number, { lastAccessed: number, subscription?: any }>();

  // Debouncing for scroll-to-load requests
  private scrollSubject = new Subject<number>();
  
  // Cleanup subject for managing subscriptions
  private destroy$ = new Subject<void>();

  threadInteractions$: Observable<InteractionThread[]> = (this.teamInboxState$.pipe(
    filter((state: any) => !!state.focusedUser),
    distinctUntilChanged((a, b) => {
      return true
        && a?.focusedUser == b?.focusedUser
        && a?.pendingThreadInteractions.length === b?.pendingThreadInteractions.length
        && a?.pendingThreadInteractions[0]?.interaction.status === b?.pendingThreadInteractions[0]?.interaction.status
        && a?.threadInteractions.nextPagesThreads.length == b?.threadInteractions.nextPagesThreads.length
        && a?.threadInteractions.lastCleanupTime == b?.threadInteractions.lastCleanupTime // React to cleanup
        && a?.threadInteractions.fetching === b?.threadInteractions.fetching; // React to fetch completion
    }),
    tap(state => {
      // Update access time for cache tracking
      if (state.focusedUser) {
        this.conversationCaches.set(state.focusedUser, {
          lastAccessed: Date.now(),
          subscription: this.conversationCaches.get(state.focusedUser)?.subscription
        });
      }
    }),
    switchMap((state) => {
      return this.teamInteractionRepository.getLocalTeamMessagesWindow({
        page: state.threadInteractions.page,
        userId: state.focusedUser,
        windowSize: state.threadInteractions.windowSize || 100, // Use dynamic window size
        is_room: false // DM conversations are always is_room: false
      }).pipe(
        map((messages: InteractionThread[]) => {
          const pendingMessages = state.pendingThreadInteractions.map((p: any) => p.interaction).filter((i: any) => i.user_id == state.focusedUser as any);
          const nextPageMessages = state.threadInteractions.nextPagesThreads.filter((i: any) => i.user_id == state.focusedUser as any);
          
          // Combine and deduplicate messages by interaction_id
          const allMessages: InteractionThread[] = [...pendingMessages, ...messages, ...nextPageMessages];
          const uniqueMessages = allMessages.reduce((acc: InteractionThread[], msg: InteractionThread) => {
            const existingIndex = acc.findIndex((m: InteractionThread) => m.interaction_id === msg.interaction_id);
            if (existingIndex === -1) {
              acc.push(msg);
            }
            return acc;
          }, [] as InteractionThread[]);
          
          // Sort and limit to current window size
          const finalMessages = uniqueMessages
            .sort((a: InteractionThread, b: InteractionThread) => b.interaction_dt.getTime() - a.interaction_dt.getTime())
            .slice(0, state.threadInteractions.windowSize || 100); // Use dynamic window size
            
          return finalMessages;
        })
      );
    }),
    takeUntil(this.teamInboxState$.pipe(first(s => !s.focusedUser))),
    sampleTime(100),
    tap((messages) => {
      this.updateReadStatus(messages);
    })
    // Removed shareReplay to fix subscription issues after page refresh
  ) as Observable<InteractionThread[]>);

  readonly profileRooms$ = this.store.select(participantRooms)
  readonly tenantRooms$ = this.store.select(tenantRooms);
  readonly selectedRoom$ = this.store.select(selectedRoom);
  tempActiveRoom$ = new BehaviorSubject<IRoom>({ room_name: '', private: false });
  editingRoom$ = new BehaviorSubject<IRoom | undefined>(undefined);
  selectedTranscriptions$ = new BehaviorSubject<InteractionTranscription | null>(null);
  isYobiOne = new BehaviorSubject(false);

  get isRoom() {
    return !!this.tempActiveRoom$.value?.room_id
  }

  roomThreadInteractions$ = this.selectedRoom$.pipe(
    filter((room) => !!room?.room_id),
    switchMap((room) =>
      this.teamInboxState$.pipe(
        map(state => state.threadInteractions.page),
        distinctUntilChanged(),
        switchMap((page) => this.teamInteractionRepository.getLocalTeamMessages({
          page, userId: (room?.room_id) as any, is_room: true
        }))
      )
    ),
    tap((messages) => this.updateReadStatus(messages)),
  );

  activeUsers$: Observable<TeamMember[]> = combineLatest([
    this.store.select(selectTeamChatActiveUsers),
    this.recentInteractions$
  ]).pipe(map(([users, interactions]) => users.map(u => ({
    ...u,
    metadata: {
      unread: interactions.find(i => i.interaction.user_id as any == u.user_id)?.interaction.status == 'unread'
    }
  }))))

  activeUser$ = combineLatest([this.teamMembers$, this.store.select(selectActiveUser)]).pipe(
    map(([teamMembers, user]) => user ? ({ ...user, active: teamMembers.find(m => m.user_id == user.user_id)?.active }) : undefined)
  )

  remainigUsers$: Observable<TeamMember[]> = combineLatest([
    this.recentInteractions$,
    this.teamMembers$
  ]).pipe(map(([recentInteractions, teamMembers]) => {
    const recentInteractionUsers = recentInteractions.map(i => i.interaction.user_id as any)
    return teamMembers.filter(m => !recentInteractionUsers.includes(m.user_id))
  }))

  remainingParticipantRooms$ = combineLatest([
    this.store.select(tenantRooms),
    this.store.select(participantRooms),
    this.recentInteractions$,
  ]).pipe(
    map(([tenantRooms, participantRooms, recentInteractions]) => {
      const recentInteractionsRoomsIds = recentInteractions.filter(i => !!i.room_id).map(i => i.room_id)
      const participantsRoomsIds = participantRooms.map(i => i.room_id)
      return [
        ...participantRooms
          .filter((room: any) => !recentInteractionsRoomsIds.includes(room.room_id))
          .map(room => ({ ...room, isCurrentUserParticipant: true })),
        ...tenantRooms
          .filter(room => !participantsRoomsIds.includes(room.room_id))
      ]
    })
  );

  interactionType$ = new BehaviorSubject<'all' | 'groups' | 'personal'>('all');
  replyToInteractionId$ = new BehaviorSubject<number | null>(null);

  async openTeamChatThread(userId: number, idle = false) {
    const users = await firstValueFrom(this.teamMembers$) ?? [];
    
    const user = users.find(u => u.user_id == userId);
    
    if (user) {
      this.store.dispatch(OpenTeamChatThreadAction({ payload: { user, idle } }))
      this.FetchTeamChatMessages({ page: 1, userId, users });
    } else if (this.isYobiOne?.value && !user) {
      // Find the Yobi One bot user
      const yobiOneBot = users.find((item) => item.is_bot && item.bot_type === 'assistant');
      
      if (yobiOneBot) {
        this.store.dispatch(OpenTeamChatThreadAction({ payload: { user: yobiOneBot, idle } }))
        this.FetchTeamChatMessages({ page: 1, userId: yobiOneBot.user_id, users });
      } else {
        throw new Error("Yobi One bot user not found");
      }
    }
    else {
      throw new Error("User not found");
    }
  }

  async openRoomTeamChatThread(roomId: string, idle = false) {
    const users = await firstValueFrom(this.teamMembers$) ?? [];
    this.store.dispatch(OpenTeamChatThreadAction({ payload: { user: undefined, idle } }));
    this.FetchTeamChatRoomMessages({ page: 1, userId: 0, roomId, users });
  }

  setActiveRoomStore(room: IRoom): void {
    this.store.dispatch(SetActiveRoomAction({ payload: { room_id: room.room_id, room_name: room.room_name, private: room.private, avatar: room.avatar } }));
  }

  fetchTeamRecentMessages(type: 'all' | 'groups' | 'personal', page?: number) {
    this.interactionType$.next(type);
    page = page ?? this.recentInteractionsPage$.value + 1;
    this.store.dispatch(FetchTeamRecentMessagesAction({ payload: { page, page_size: 25, type } }));
  }

  async updateReadStatus(messages: InteractionThread[]) {
    const message = messages[0];
    if (message?.direction == InteractionDirection.inbound && message.status == InteractionStatus.unread && message.interaction_id) {
      this.store.dispatch(UpdateTeamChatMessageStatusAction({
        payload: {
          interation_id: message.interaction_id, data: {
            receiver_id: this.isRoom ? this.tempActiveRoom$.value.room_id! : message.user_id,
            status: InteractionStatus.read
          }
        }
      }));
    }
  }

  async updateLocalRoomRecentInteractions(roomId: string, data: Partial<IRoom>) {
    const rooms = await firstValueFrom(this.profileRooms$)
    const room = rooms.find(({ room_id }) => room_id == roomId)
    room && this.teamInteractionRepository.updateLocalRoomRecentInteractions({
      room_id: roomId,
      room_name: data.room_name ?? room.room_name as string,
      private: data.private ?? room.private as boolean,
      avatar: data.avatar ?? room.avatar,
    })
  }

  setEditingRoom(room: IRoom) {
    this.editingRoom$.next(room)
    this.store.dispatch(SetRightPanelActiveWindowAction({ payload: RightPanelIndexes.users }))
  }

  FetchTeamChatRoomMessages(query: { page: number, userId: number, roomId: string, users?: TeamMember[] },) {
    this.store.dispatch(FetchTeamChatMessagesAction({ payload: { ...query, is_room: true } }));
  }

  FetchTeamChatMessages(query: { page: number, userId: number, users?: TeamMember[] }) {
    this.store.dispatch(FetchTeamChatMessagesAction({ payload: { ...query, is_room: false } }))
  }

  fetchProfileRooms() {
    this.store.dispatch(FetchParticipantRoomsAction({ payload: {} }));
  }

  fetchTenantRooms() {
    this.store.dispatch(FetchTenantRoomsAction({ payload: {} }));
  }

  closeTeamChatThread(userId: number) {
    this.store.dispatch(CloseTeamChatThreadAction({ payload: { userId } }));
    // Clean up memory for this conversation when closing
    this.cleanupConversationMemory(userId);
  }

  closeAllTeamChatThreads() {
    this.store.dispatch(CloseAllTeamChatThreadsAction())
  }

  // Expose loading and error states as observables
  expandingWindow$ = this.teamInboxState$.pipe(
    map(state => state.threadInteractions.expandingWindow || false)
  );

  expandError$ = this.teamInboxState$.pipe(
    map(state => state.threadInteractions.expandError)
  );

  // Method to be called when user scrolls near the top of message list
  // Now debounced to prevent rapid API calls
  onScrollNearTop(userId: number) {
    this.scrollSubject.next(userId);
  }

  // Expand message window when user scrolls up to load more messages
  expandMessageWindow(userId: number, additionalSize: number = 50) {
    this.store.dispatch(ExpandMessageWindowAction({ payload: { userId, additionalSize } }));
  }

  // Cleanup old conversation memory (called on conversation close or periodically)
  cleanupConversationMemory(userId?: number, maxAge?: number) {
    // Clean state cache (this will trigger the effect which calls cleanupObservableCache)
    this.store.dispatch(CleanupConversationMemoryAction({ payload: { userId, maxAge } }));
  }

  // Public method to be called by effect (to avoid circular calls)
  cleanupObservableCache(userId?: number, maxAge?: number) {
    this.cleanupObservableCacheInternal(userId, maxAge);
  }

  // Clean up observable caches (internal implementation)
  private cleanupObservableCacheInternal(userId?: number, maxAge?: number) {
    const maxAgeMs = maxAge || (30 * 60 * 1000); // Default 30 minutes
    const now = Date.now();
    if (userId) {
      // Clean specific conversation
      const cache = this.conversationCaches.get(userId);
      if (cache && (now - cache.lastAccessed > maxAgeMs)) {
        this.conversationCaches.delete(userId);
        // Cleaned observable cache
      }
    } else {
      // Clean all old conversations
      const toDelete: number[] = [];
      this.conversationCaches.forEach((cache, key) => {
        if (now - cache.lastAccessed > maxAgeMs) {
          toDelete.push(key);
        }
      });
      
      toDelete.forEach(key => {
        this.conversationCaches.delete(key);
        // Cleaned observable cache for user
      });
    }
  }

  // Clear expand error (for UI error handling)
  clearExpandError() {
    // This could be implemented as a separate action if needed
  }

  deleteInteraction(interaction_id: number) {
    this.store.dispatch(DeleteTeamChatMessageAction({ payload: { interaction_id } }));
  }

  reactToInteraction(interaction_id: number, reaction: string) {
    this.store.dispatch(ReactToTeamChatMessageAction({ payload: { reaction, interaction_id } }));
  }

  async sendMessage({ message, attachments, is_room, observer, participants_ids }: { message: string, attachments: FileModel[], is_room: boolean, observer: Observer<any>, participants_ids: string[] }) {
    let receiver_id = is_room ? this.tempActiveRoom$.getValue()['room_id'] : (await firstValueFrom(this.teamInboxState$)).focusedUser;
    let _receiver_id = receiver_id;
    if (this.isYobiOne && !receiver_id) {
      _receiver_id = (await firstValueFrom(this.teamMembers$)).filter((item) => {
        if (item.is_bot && item.bot_type === 'assistant') return item.user_id;
      })[0].user_id;
    }
    let user_id = (await firstValueFrom(this.store.select(selectTenantProfile)))!.userId
    const replyToInteractionId = await firstValueFrom(this.replyToInteractionId$);
    const teamMessageBody = new TeamMessageBody(
      _receiver_id,
      message,
      is_room,
      'message',
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      new Date().getTime(),
      user_id,
      attachments,
      participants_ids,
      replyToInteractionId?.toString()
    )
    if (is_room) {
      const temp = this.temporarySetMessage(teamMessageBody.msg_id, is_room, user_id, receiver_id, attachments, message);
      this.store.dispatch(SendTeamChatMessageSuccessAction({ payload: { teamInteraction: [temp as any], msg_id: teamMessageBody.msg_id } }));
    }
    this.store.dispatch(SendTeamChatMessageAction({ payload: teamMessageBody }))
    // Clear reply state after sending
    this.clearReply();
    this.createTeamMessageUseCase.execute({ body: teamMessageBody, observer })
      .pipe(
        map((teamInteraction) => {
          //Temporary fix for backend issue.
          if (teamInteraction.status == InteractionStatus.unread) {
            teamInteraction.status = InteractionStatus.read
          }
          return SendTeamChatMessageSuccessAction({ payload: { teamInteraction: [teamInteraction], msg_id: teamMessageBody.msg_id || 0 } })
        }),
        tap((item) => {
          this.teamInteractionRepository.deleteTeamThreadInteractions({ user_id: receiver_id, interaction_id: item.payload.msg_id })
        }),
        catchError((error: any) => {
          let errorStr = error.responseError?.error?.message || 'Operation was not possible';
          return of(SendTeamChatMessageFailAction({ payload: { errorStr, msg_id: teamMessageBody.msg_id } }));
        }),
        tap(action => {
          this.store.dispatch(action)
        })
      ).subscribe();
  }

  createRoom(data: InputCreateRoom) {
    this.store.dispatch(CreateRoomAction({ payload: { room_name: data.room_name, room_private: data.private, participants_ids: data.participants_ids } }));
  }

  fetchRoomParticipant(room_id: string) {
    return this.teamInteractionRepository.fetchRoomParticipant(room_id);
  }

  joinRoom(room_id: string, participants: (string | number)[]) {
    return this.teamInteractionRepository.addRoomPartcipant(room_id, participants);
  }

  addNewParticipantToRoom(room_id: string, participants: string[]) {
    this.store.dispatch(AddRoomParticipantAction({ payload: { room_id, participants_ids: participants } }));
  }

  removeParticipantToRoom(room_id: string, participant_id: string, isCurrentUser?: boolean) {
    this.store.dispatch(RemoveRoomParticipantAction({ payload: { room_id, participant_id, isCurrentUser } }))
  }

  updateRoom(room_id: string, data: InputCreateRoom) {
    this.store.dispatch(UpdateRoomAction({ payload: { room_id, room_name: data.room_name, room_private: data.private, participants_ids: data.participants_ids } }))
  }

  updateRoomImage(room_id: string, data: FormData, progress$: Observer<any>) {
    return this.teamInteractionRepository.updateRoomImage(room_id, data, progress$).pipe(
      tap(({ avatar }) => {
        this.snackBarService.openAlert({
          message: `Room logo has been updated.`,
          type: "success"
        });
        this.updateLocalRoomRecentInteractions(room_id, { avatar })
      }),
      catchError((httpError: ErrorApiReponse<any>) => {
        this.snackBarService.openAlert({
          message: httpError.message,
          type: 'failure'
        });
        throw httpError;
      }));
  }

  async resend({ msg_id, observer }: { msg_id: number, observer: Observer<any> }) {
    const state = await firstValueFrom(this.teamInboxState$)
    const { body, interaction } = state.pendingThreadInteractions.find(i => i.body.msg_id === msg_id)!
    const teamMessageBody = new TeamMessageBody(
      body.getBody().receiver_id,
      body.getBody().data,
      false,
      'message',
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      body.msg_id,
      body.getBody().user_id!,
      body.attachments,
      []
    )
    this.store.dispatch(ResendTeamChatMessageAction({ payload: { msg_id } }))
    this.createTeamMessageUseCase.execute({ body: teamMessageBody, observer })
      .pipe(
        map((teamInteraction) => SendTeamChatMessageSuccessAction({ payload: { teamInteraction: [teamInteraction], msg_id: teamMessageBody.msg_id || 0 } })),
        catchError((error: any) => {
          let errorStr = error.responseError?.error?.message || 'Operation was not possible';
          return of(SendTeamChatMessageFailAction({ payload: { errorStr, msg_id: teamMessageBody.msg_id } }));
        }),
        tap(action => {
          this.store.dispatch(action)
        })
      ).subscribe();
  }

  deleteTeamRecentInteraction(key: string, id: string): void {
    this.teamInteractionRepository.deleteTeamRecentInteraction(key, id);
  }

  updateRoomReadStatus(interaction_id: any, receiver_id: string) {
    return this.teamInteractionRepository.updateMessageStatus({ interation_id: interaction_id, data: { receiver_id, status: InteractionStatus.read } }).pipe(
      tap((result) => {
        this.teamInteractionRepository.updateInteractionStatus(result.interaction_id, InteractionStatus.read);
      })
    ).subscribe();
  }

  temporarySetMessage(msg_id: number, is_room: boolean, user_id: number | undefined, receiver_id: string, attachments: any[], data: string) {
    const _stash = {
      created_dt: new Date(),
      direction: 'outgoing',
      is_room: is_room,
      page: 1,
      interaction_dt: new Date().toUTCString(),
      interaction_id: msg_id,
      status: 'read',
      type: "message",
      avatar: '',
      user_id: user_id,
      sender_id: user_id,
      receiver_id: receiver_id,
      data: data,
      attachments: attachments
    }
    return _stash;
  }

  getInteractionTranscription(interaction_id: number) {
    return this.teamInteractionRepository.getInteractionTranscription(interaction_id);
  }

  ngOnDestroy(): void {
    // Complete all subjects to prevent memory leaks
    this.destroy$.next();
    this.destroy$.complete();
    
    // Complete other subjects
    this.scrollSubject.complete();
    
    // Complete BehaviorSubjects
    this.tempActiveRoom$.complete();
    this.editingRoom$.complete();
    this.teamMembersLoaded$.complete();
    this.isYobiOne?.complete();
    
    // Clear conversation caches
    this.conversationCaches.clear();
    
    console.log('TeamChatViewModel destroyed and cleaned up');
  }

  setReplyToInteraction(interactionId: number): void {
    this.replyToInteractionId$.next(interactionId);
  }

  clearReply(): void {
    this.replyToInteractionId$.next(null);
  }
}
